import {
    GET_GSLA_FOR_WAREHOUSE,
    GET_GSLA_RECOMMENDATIONS_FOR_WAREHOUSE,
    PUBLISH_GSLA_FOR_WAREHOUSE,
    GET_USER_PERMISSIONS,
    GET_GSLA_FOR_MULTIPLE_WAREHOUSES,
    GET_GSLA_RECOMMENDATIONS_FOR_MULTIPLE_WAREHOUSE,
    GET_R2F_THRESHOLDS,
    GET_R2F_THRESHOLDS_FOR_WAREHOUSE,
    GET_ENTIRE_NETWORK_R2F_THRESHOLDS,
    PUBLISH_R2F_THRESHOLDS,
    DELETE_GSLA,
    DELETE_R2F_THRESHOLDS,
    GET_PC_FOR_WAREHOUSE_LIST,
    GET_CORA_DATA
} from 'src/core/config/APIRoutes';
import { CORAInput, GetCORADataRequest, GetCORADataResponse } from 'src/features/types/coraTypes';
import { UserPermissions } from 'src/features/types/backendApi';
import {
    GSLAApiResponse,
    GSLAPublishRequest,
    GSLAToPublish,
    GetGSLAForWarehouseRequest,
    GetGSLAForWarehouseResponse,
    GetGSLARequest,
    GetGSLAResponse,
    GetGSLARecommendationForWarehouseRequest,
    GetGSLARecommendationForWarehouseResponse,
    GetGSLARecommendationsRequest,
    GetGSLARecommendationsResponse,
    GetR2FThresholdsRequest,
    GetR2FThresholdsResponse,
    GetR2FThresholdsForWarehouseRequest,
    GetR2FThresholdsForWarehouseResponse,
    GetEntireNetworkR2FThresholdsRequest,
    GetEntireNetworkR2FThresholdsResponse,
    PublishR2FThresholdsRequest,
    PublishR2FThresholdsResponse,
    DeleteGSLARequest,
    DeleteGSLAResponse,
    DeleteR2FThresholdsRequest,
    DeleteR2FThresholdsResponse,
    GetPCForWarehouseListRequest,
    GetPCForWarehouseListResponse
} from 'src/features/types/gslaApi';
import { getAPIHostWithPrefix, getBackendAPIHostWithPrefix } from 'src/utils/Environment';
import fetch from 'src/utils/APIGatewayClients';

export function getSiteGSLA(warehouseId: string): Promise<GetGSLAForWarehouseResponse> | undefined
{
    if (!warehouseId) return;
    const requestBody: GetGSLAForWarehouseRequest = { warehouseId };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getMultipleSitesGSLA(warehouseIds: string[], preserveDefaultTypes?: boolean): Promise<GetGSLAResponse> | undefined
{
    if (!warehouseIds) return;
    const requestBody: GetGSLARequest = {
        warehouseIds,
        ...(preserveDefaultTypes !== undefined && { preserveDefaultTypes })
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_FOR_MULTIPLE_WAREHOUSES}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getSiteGSLARecommendations(warehouseId: string): Promise<GetGSLARecommendationForWarehouseResponse> | undefined
{
    if (!warehouseId) return;
    const requestBody: GetGSLARecommendationForWarehouseRequest = { warehouseId };
    
    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_RECOMMENDATIONS_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getMultipleSitesGSLARecommendations(warehouseIds: string[]): Promise<GetGSLARecommendationsResponse> | undefined
{
    if (!warehouseIds) return;
    const requestBody: GetGSLARecommendationsRequest = { warehouseIds };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_GSLA_RECOMMENDATIONS_FOR_MULTIPLE_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function publishGSLAValues(requestBody: GSLAPublishRequest): Promise<GSLAApiResponse> | undefined
{
    // Stringify the request body
    const requestBodyString = JSON.stringify(requestBody);
    
    return fetch(
        `${getAPIHostWithPrefix()}${PUBLISH_GSLA_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: requestBodyString, // Use the stringified body
        },
    );
}

export function getUserPermissions(userId: string): Promise<UserPermissions> | undefined
{
    const requestBody = { userId };

    return fetch(
        `${getBackendAPIHostWithPrefix()}${GET_USER_PERMISSIONS}`,
        {
            method: 'POST',
            body: JSON.stringify(requestBody),
        },
    );
}

export function getR2FThresholds(warehouseIds: string[]): Promise<GetR2FThresholdsResponse> | undefined
{
    if (!warehouseIds) return;
    const requestBody: GetR2FThresholdsRequest = { 
        warehouseIds,
        preserveDefaultTypes: true 
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getR2FThresholdsForWarehouse(warehouseId: string, preserveDefaultTypes: boolean = true): Promise<GetR2FThresholdsForWarehouseResponse> | undefined
{
    if (!warehouseId) return;
    const requestBody: GetR2FThresholdsForWarehouseRequest = { 
        warehouseId,
        preserveDefaultTypes
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_R2F_THRESHOLDS_FOR_WAREHOUSE}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getEntireNetworkR2FThresholds(preserveDefaultTypes: boolean = true): Promise<GetEntireNetworkR2FThresholdsResponse> | undefined
{
    const requestBody: GetEntireNetworkR2FThresholdsRequest = {
        preserveDefaultTypes
    };

    return fetch(
        `${getAPIHostWithPrefix()}${GET_ENTIRE_NETWORK_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function publishR2FThresholds(requestBody: PublishR2FThresholdsRequest): Promise<PublishR2FThresholdsResponse> | undefined
{
    // Ensure preserveDefaultTypes is set to true
    const updatedRequestBody = {
        ...requestBody,
        preserveDefaultTypes: true
    };

    // Publishing R2F thresholds
    
    // Filter out Default_Network_PC changes for non-DEFAULT warehouses
    // This prevents saving Default_Network_PC changes to warehouse-specific configs
    // while still allowing the UI to show Default_Network_PC for visibility
    if (updatedRequestBody.r2fToPublishList) {
        updatedRequestBody.r2fToPublishList.forEach(item => {
            // If we're updating an FC warehouse (not 'DEFAULT')
            if (item.warehouseId && item.warehouseId !== 'DEFAULT') {
                // And if we have a Default_Network_PC in the r2fToPublish
                if (item.r2fToPublish && 'Default_Network_PC' in item.r2fToPublish) {
                    // Remove Default_Network_PC from FC warehouse request
                    // This prevents saving Default_Network_PC changes to warehouse-specific config
                    // The UI restrictions should already prevent these changes from being made
                    delete item.r2fToPublish['Default_Network_PC'];
                }
            }
            // If warehouseId is 'DEFAULT', we allow Default_Network_PC to be saved
        });
    }
    
    // Prepare final request for submission

    return fetch(
        `${getAPIHostWithPrefix()}${PUBLISH_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedRequestBody),
        },
    );
}

export function deleteGSLA(requestBody: DeleteGSLARequest): Promise<DeleteGSLAResponse> | undefined
{
    return fetch(
        `${getAPIHostWithPrefix()}${DELETE_GSLA}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function deleteR2FThresholds(requestBody: DeleteR2FThresholdsRequest): Promise<DeleteR2FThresholdsResponse> | undefined
{
    return fetch(
        `${getAPIHostWithPrefix()}${DELETE_R2F_THRESHOLDS}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getPCForWarehouseList(warehouseIds: string[]): Promise<GetPCForWarehouseListResponse> | undefined
{
    if (!warehouseIds || warehouseIds.length === 0) return;
    const requestBody: GetPCForWarehouseListRequest = { warehouseIds };

    return fetch(
        `${getBackendAPIHostWithPrefix()}${GET_PC_FOR_WAREHOUSE_LIST}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        },
    );
}

export function getCORAData(input: CORAInput): Promise<GetCORADataResponse> {
    const requestBody = {
        warehouseIds: input.nodeIds,
        fulfillmentProgram: input.fulfillmentProgram,
        modeSelector: input.modeSelector,
        durationOfRiskWindowInDays: input.durationOfRiskWindowInDays
    };

    return fetch(`${getBackendAPIHostWithPrefix()}${GET_CORA_DATA}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
    }).then(data => {
        const transformedData: GetCORADataResponse = {
            nodeFailures: data.nodeFailures || {},
            riskDataByNode: {}
        };

        if (data.riskDataByNode) {
            Object.keys(data.riskDataByNode).forEach(nodeKey => {
                const nodeData = data.riskDataByNode[nodeKey];
                transformedData.riskDataByNode[nodeKey] = {
                    ...nodeData,
                    nodeId: nodeKey
                };
            });
        }

        return transformedData;
    });
}
